import { useRef, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { alpha } from '@classify/react-core-components/styles';

import { makeStyles } from '@mui/styles';
import { palette as themePalette } from '@openclassrooms/ui-web/theme';
import Typography from '@classify/react-core-components/Typography';
import useScrollAnimation, {
  hasReachedEndOfPage,
  ANIMATION_DURATION,
} from '../../hooks/useScrollAnimation';
import { ChapterType } from '../../types';

const useStyles = makeStyles(({ spacing, palette, zIndex, breakpoints }) => ({
  root: {
    display: 'flex',
    padding: 0,
    width: 'auto',
    left: 0,
    right: 0,
    transition: `bottom ${ANIMATION_DURATION}ms`,
    zIndex: zIndex.appBar,
    backgroundColor: palette.background.primary,
  },
  labelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%',
    height: '100%',
    borderTop: '1px solid',
    marginLeft: 'auto',
    padding: spacing(3, 5),
  },
  courseLabel: {
    display: 'none',
    [breakpoints.up('sm')]: {
      display: 'block',
      color: palette.text.primary,
      textAlign: 'right',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: '60ch',
      marginTop: spacing(1),
      whiteSpace: 'nowrap',
    },
  },
}));

export interface Props {
  categoryColor?: string;
  children: ReactNode;
  nextChapter: ChapterType;
}

const CourseNavigationFooter = ({
  categoryColor = themePalette.brand.main,
  children,
  nextChapter,
}: Props) => {
  const classes = useStyles();
  const footerRef = useRef<HTMLElement>(null);
  const [endOfPageOnLoad, setEndOfPageOnLoad] = useState(false);

  const visibleOffsetHeight = '0px';
  const hiddenOffsetHeight = `-${footerRef?.current?.clientHeight}px`;

  const [offset, setOffset] = useScrollAnimation({
    toggleVisibilityCallback: (isVisible) => {
      const offsetHeight = isVisible ? visibleOffsetHeight : hiddenOffsetHeight;

      setOffset(offsetHeight);
    },
    hideAtTopOfPage: true,
    showAtEndOfPage: true,
    initialOffset: hiddenOffsetHeight,
  });

  useEffect(() => {
    // On the initial load, the scrollHeight is smaller and then increases when the content is injected
    setEndOfPageOnLoad(hasReachedEndOfPage() && document.documentElement.scrollTop === 0);

    if (!endOfPageOnLoad) {
      setOffset(hiddenOffsetHeight);
    }
  }, [setOffset, endOfPageOnLoad, hiddenOffsetHeight]);

  useEffect(
    function hideFooterOnChapterChange() {
      setOffset(hiddenOffsetHeight);
    },
    [nextChapter, setOffset, hiddenOffsetHeight]
  );

  return (
    <footer
      className={classes.root}
      style={{
        bottom: endOfPageOnLoad ? '0' : offset,
        position: endOfPageOnLoad ? 'absolute' : 'sticky',
      }}
      ref={footerRef}
      onFocus={() => setOffset(visibleOffsetHeight)}
      onBlur={() => setOffset(hiddenOffsetHeight)}
    >
      <div
        className={classes.labelWrapper}
        style={{
          backgroundColor: alpha(categoryColor, 0.08),
          borderColor: alpha(categoryColor, 0.32),
        }}
      >
        {children}

        {nextChapter ? (
          <Typography className={classes.courseLabel} variant="subtitle2" component="p">
            {nextChapter.title}
          </Typography>
        ) : null}
      </div>
    </footer>
  );
};

export default CourseNavigationFooter;
